import { useEffect, useState, useCallback, useRef } from "react";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { RefreshCw, TrendingUp, TrendingDown, Coins, DollarSign, CircleDollarSign } from "lucide-react";

const REFRESH_MS = 20000;

const fmtTRY = (n) => (n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtUSD = (n) => (n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtGram = (n) => (n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 4, maximumFractionDigits: 4 });

function DegisimBadge({ d }) {
  if (!d) return null;
  const neg = String(d).includes("-");
  const Icon = neg ? TrendingDown : TrendingUp;
  const col = neg ? "#A63A3A" : "#2F6E4E";
  return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold" style={{ color: col }}>
      <Icon className="w-3.5 h-3.5" /> {d}
    </span>
  );
}

function PriceCard({ label, alis, satis, degisim, unit, accent, big, testId }) {
  return (
    <div className={`bg-white border rounded-sm p-4 ${big ? "border-[#B8933F]" : "border-[#E7DECC]"}`} style={big ? { boxShadow: "0 0 0 1px #B8933F22" } : undefined} data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <div className="kicker" style={accent ? { color: accent } : undefined}>{label}</div>
        <DegisimBadge d={degisim} />
      </div>
      <div className={`font-heading font-bold tabular-nums ${big ? "text-3xl" : "text-xl"}`} style={{ color: accent || "#26231E" }}>
        {satis}<span className="text-sm font-normal text-[#8C8377] ml-1">{unit}</span>
      </div>
      {alis != null && <div className="text-xs text-[#8C8377] mt-1 tabular-nums">Alış: {alis} {unit}</div>}
    </div>
  );
}

export default function AltinPanel() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState(REFRESH_MS / 1000);
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/altin/fiyatlar");
      setData(data);
      setErr(false);
    } catch (e) {
      setErr(true);
    } finally {
      setLoading(false);
      setCountdown(REFRESH_MS / 1000);
    }
  }, []);

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, REFRESH_MS);
    return () => clearInterval(timerRef.current);
  }, [load]);

  useEffect(() => {
    const t = setInterval(() => setCountdown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, []);

  const a = data?.altin || {};
  const usdGram = data?.usd_gram_altin || {};
  const usdGramHas = data?.usd_gram_has_altin || {};
  const usd = data?.kurlar?.USD || {};
  const eur = data?.kurlar?.EUR || {};

  return (
    <Layout>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <div className="kicker text-[#B8933F] flex items-center gap-2"><Coins className="w-4 h-4" /> ALTIN PANELİ</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-[#26231E]">Canlı Altın & Döviz</h1>
          <p className="text-sm text-[#8C8377] mt-1">{data?.kaynak || "Canlı piyasa"} · Son güncelleme: <b>{data?.guncelleme || "—"}</b></p>
        </div>
        <button onClick={load} data-testid="altin-refresh" className="inline-flex items-center gap-2 px-4 py-2 border border-[#B8933F] text-[#B8933F] rounded-sm text-sm font-medium hover:bg-[#B8933F]/5 transition-colors duration-150">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Yenile <span className="text-xs text-[#8C8377]">({countdown}s)</span>
        </button>
      </div>

      {err && (
        <div className="mb-4 p-3 border border-[#A63A3A]/30 bg-[#A63A3A]/5 text-[#A63A3A] rounded-sm text-sm" data-testid="altin-error">
          Canlı fiyat kaynağına şu an ulaşılamadı. Otomatik olarak {REFRESH_MS / 1000} sn'de bir tekrar denenecek.
        </div>
      )}

      {/* En kritik: USD/gram altın (kasa dönüşümü için) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <PriceCard big label="USD / GRAM ALTIN" satis={fmtUSD(usdGram.satis)} alis={fmtUSD(usdGram.alis)} unit="$/gr" accent="#B8933F" testId="altin-usd-gram" />
        <PriceCard big label="USD / GRAM HAS ALTIN" satis={fmtUSD(usdGramHas.satis)} alis={fmtUSD(usdGramHas.alis)} unit="$/gr" accent="#9C7A33" testId="altin-usd-gram-has" />
        <PriceCard big label="USD / TRY" satis={fmtTRY(usd.satis)} alis={fmtTRY(usd.alis)} degisim={usd.degisim} unit="₺" accent="#2F6E4E" testId="altin-usdtry" />
      </div>

      <div className="kicker mb-2">ALTIN (₺)</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <PriceCard label="GRAM ALTIN" satis={fmtTRY(a.gram_altin?.satis)} alis={fmtTRY(a.gram_altin?.alis)} degisim={a.gram_altin?.degisim} unit="₺" testId="altin-gram" />
        <PriceCard label="GRAM HAS ALTIN" satis={fmtTRY(a.gram_has_altin?.satis)} alis={fmtTRY(a.gram_has_altin?.alis)} degisim={a.gram_has_altin?.degisim} unit="₺" testId="altin-has" />
        <PriceCard label="ONS" satis={fmtUSD(a.ons?.satis)} alis={fmtUSD(a.ons?.alis)} degisim={a.ons?.degisim} unit="$" testId="altin-ons" />
        <PriceCard label="22 AYAR" satis={fmtTRY(a.yirmi_iki_ayar?.satis)} alis={fmtTRY(a.yirmi_iki_ayar?.alis)} degisim={a.yirmi_iki_ayar?.degisim} unit="₺" testId="altin-22" />
        <PriceCard label="ÇEYREK" satis={fmtTRY(a.ceyrek?.satis)} alis={fmtTRY(a.ceyrek?.alis)} degisim={a.ceyrek?.degisim} unit="₺" testId="altin-ceyrek" />
        <PriceCard label="YARIM" satis={fmtTRY(a.yarim?.satis)} alis={fmtTRY(a.yarim?.alis)} degisim={a.yarim?.degisim} unit="₺" testId="altin-yarim" />
        <PriceCard label="TAM" satis={fmtTRY(a.tam?.satis)} alis={fmtTRY(a.tam?.alis)} degisim={a.tam?.degisim} unit="₺" testId="altin-tam" />
        <PriceCard label="EUR / TRY" satis={fmtTRY(eur.satis)} alis={fmtTRY(eur.alis)} degisim={eur.degisim} unit="₺" testId="altin-eurtry" />
      </div>

      <div className="p-4 bg-[#F3ECDD] border border-[#E7DECC] rounded-sm text-sm text-[#6B6459]" data-testid="altin-info">
        <div className="flex items-center gap-2 font-semibold text-[#B8933F] mb-1"><CircleDollarSign className="w-4 h-4" /> Kasa dönüşüm mantığı (Faz 3'te kullanılacak)</div>
        Borçlunun ödediği <b>USD</b>, ödeme günündeki <b>USD/gram altın</b> fiyatına bölünerek kaç <b>gram altın</b> tahsil edildiği hesaplanır. Kasa her zaman <b>gram altın</b> cinsinden tutulur.
        Örn: 300 $ ödeme ÷ {fmtUSD(usdGram.satis) || "—"} $/gr = <b>{usdGram.satis ? fmtGram(300 / usdGram.satis) : "—"} gr</b> altın.
      </div>
    </Layout>
  );
}
